import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Download } from "lucide-react";
import { InsStatus } from "./InsurerLayout";
import { InsuranceQuoteView } from "@/components/shared/InsuranceQuoteView";
import { InsuranceSubmission } from "@/components/shared/InsuranceSubmission";
import { InsuranceThread, downloadBlob } from "@/components/shared/InsuranceThread";
import { INS_STATUSES, insApi, insFileUrl } from "@/lib/insurance";
import { errMsg, fmtDate } from "@/lib/api";

const FLOW = INS_STATUSES.filter(([k]) => k !== "quote_declined");

const Progress = ({ r }) => {
  const at = Object.fromEntries((r.status_history || []).map((h) => [h.status, h.at]));
  const idx = FLOW.findIndex(([k]) => k === (r.status === "quote_declined" ? "quote_ready" : r.status));
  return (
    <ol className="flex flex-wrap gap-2" data-testid="insurer-status-progress">
      {FLOW.map(([k, l], i) => {
        const done = i === idx || !!at[k];
        return (
          <li key={k} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${done ? "bg-[#020165] text-white" : "bg-white text-[#56585E]"}`} title={at[k] ? fmtDate(at[k]) : ""}>
            {done && <Check className="h-3 w-3" />}{l}
          </li>
        );
      })}
      {r.status === "quote_declined" && <li className="rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-700">Quote Declined</li>}
    </ol>
  );
};

const Decision = ({ r, reload }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (kind) => {
    if (kind === "decline" && !window.confirm("Decline this quote?")) return;
    setBusy(true);
    try {
      await insApi.post(`/requests/${r.id}/quote/${kind}`, kind === "decline" ? { reason } : {});
      toast.success(kind === "accept" ? "Quote accepted" : "Quote declined");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const d = r.quote?.decision;
  if (d) return <p className={`mt-4 rounded-xl p-3 text-sm font-semibold ${d.decision === "accepted" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="insurer-quote-decision">Quote {d.decision} by {d.by} on {new Date(d.at).toLocaleString("en-ZW")}{d.reason ? ` — ${d.reason}` : ""}</p>;
  if (r.status !== "quote_ready") return null;
  return (
    <div className="mt-5 border-t pt-4 space-y-3">
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, shown to Accelary if you decline)" rows={2} className="w-full rounded-lg border p-3 text-sm" data-testid="insurer-decline-reason" />
      <div className="flex flex-wrap gap-3">
        <button disabled={busy} onClick={() => decide("accept")} data-testid="insurer-accept-quote-button" className="rounded-full bg-[#020165] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Accept quote</button>
        <button disabled={busy} onClick={() => decide("decline")} data-testid="insurer-decline-quote-button" className="rounded-full border border-red-300 px-6 py-2.5 text-sm font-semibold text-red-700 disabled:opacity-60">Decline</button>
      </div>
    </div>
  );
};

export default function InsurerRequestDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => { insApi.get(`/requests/${id}`).then((res) => setR(res.data.request)).catch(() => setMissing(true)); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (missing) return <p className="text-sm text-[#56585E]" data-testid="insurer-request-not-found">Request not found. <Link to="/insurer/requests" className="underline">Back to requests</Link></p>;
  if (!r) return null;
  return (
    <div className="space-y-5" data-testid="insurer-request-detail">
      <Link to="/insurer/requests" className="inline-flex items-center gap-1 text-sm text-[#56585E] hover:text-[#020165]"><ArrowLeft className="h-4 w-4" />All requests</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0D141A]" data-testid="insurer-request-claim">{r.claim_number}</h1>
        <InsStatus status={r.status} testId="insurer-request-status" />
        <span className="text-sm text-[#56585E]">Accelary ref {r.reference} · submitted {fmtDate(r.created_at)}</span>
      </div>
      <Progress r={r} />
      {r.assessment && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="insurer-assessment-info">Assessment booked for {fmtDate(r.assessment.date)} ({r.assessment.slot}).</p>}
      <section className="rounded-2xl bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="font-display font-bold text-lg">Accelary quote</h2>
          {r.quote && <button onClick={() => downloadBlob(insApi, `/requests/${r.id}/quote.pdf`, `Accelary-quote-${r.reference}.pdf`)} data-testid="insurer-quote-pdf-button" className="inline-flex items-center gap-2 rounded-full border border-[#020165] px-4 py-2 text-sm font-semibold text-[#020165] hover:bg-[#020165] hover:text-white transition-colors"><Download className="h-4 w-4" />Download PDF</button>}
        </div>
        {r.quote ? <><InsuranceQuoteView quote={r.quote} testId="insurer-quote" /><Decision r={r} reload={load} /></>
          : <p className="text-sm text-[#56585E]" data-testid="insurer-quote-pending">{r.quote_history?.length ? "Accelary is preparing a revised quote. We will email you when it is ready." : "No quote yet. We will email you as soon as it is ready."}</p>}
        {r.quote_history?.length > 0 && (
          <details className="mt-5 border-t pt-4" data-testid="insurer-quote-history">
            <summary className="cursor-pointer text-sm font-semibold text-[#020165]">Previous quotes ({r.quote_history.length})</summary>
            <div className="mt-4 space-y-6">{r.quote_history.map((h, i) => (
              <div key={i} className="opacity-80"><InsuranceQuoteView quote={h} testId={`insurer-quote-history-${i}`} />
                {h.decision && <p className="mt-2 text-xs text-[#56585E]">{h.decision.decision} by {h.decision.by} on {new Date(h.decision.at).toLocaleString("en-ZW")}{h.decision.reason ? ` — ${h.decision.reason}` : ""}</p>}</div>
            ))}</div>
          </details>
        )}
      </section>
      <InsuranceThread client={insApi} basePath={`/requests/${r.id}`} uploadPath="/files/upload" fileUrl={insFileUrl} me="insurer" title="Messages with Accelary" />
      <InsuranceSubmission r={r} photoUrl={insFileUrl} />
    </div>
  );
}
